/**
 * Sourcing section — section 5 of the Style File, over the locked BOM.
 *
 * Rewritten around one question per material: **"can I cut with this, and if
 * not, what do I do?"** The previous screen answered that with nine columns and
 * up to five identically-styled buttons per row, leaving the user to infer the
 * workflow; it also assumed a single path (raise PO → receive → issue), so a
 * factory already holding the fabric had nowhere to go.
 *
 * Now each material shows what it needs, a plain-English sentence, and the one
 * obvious next step. The numbers and the alternative actions live behind a
 * single expand toggle rather than a hidden menu — a non-technical user gets
 * one visible choice, and everything else is one predictable click away.
 *
 * "I already have this" is a first-class action here: it records real warehouse
 * stock (creating the item/warehouse and enabling stock tracking if that setup
 * was never done) so the rest of Sourcing can see it.
 *
 * @see packages/domain/src/garment/materials — `materialPlan`, `declareMaterialInStock`
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronRight,
  Lock,
  PackageCheck,
  PackagePlus,
  Scissors,
  TriangleAlert,
  TruckIcon,
  Warehouse,
} from "lucide-react";
import {
  Button,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  toast,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { paiseToInr, rupeesToPaiseString, milliToQty } from "../../../lib/format.js";
import type { StyleStatus } from "../lib/status.js";

interface MaterialsSectionProps {
  styleId: string;
  styleStatus: StyleStatus;
}

type MaterialPlan = "issued" | "ready" | "partly_ready" | "awaiting" | "to_source";

/** Statuses at or past the PP gate — cutting is cleared. */
const PAST_PP_GATE = new Set<string>(["pp_approved", "in_production", "shipped", "closed"]);

type Row = {
  bomLineId: string;
  kind: string;
  label: string;
  itemId: string | null;
  uom: string;
  requiredQtyMilli: number;
  onHandQtyMilli: number;
  orderedQtyMilli: number;
  receivedQtyMilli: number;
  issuedQtyMilli: number;
  shortByMilli: number;
  availableQtyMilli: number;
  stillNeededQtyMilli: number;
  toOrderQtyMilli: number;
  vendorName: string | null;
  poCount: number;
  primaryPurchaseOrderId: string | null;
  plan: MaterialPlan;
};

type VendorOption = { id: string; name: string };

const KIND_GROUPS = [
  { key: "fabric", label: "Fabric" },
  { key: "trim", label: "Trims" },
  { key: "packing", label: "Packing" },
] as const;

const PLAN_TONE: Record<MaterialPlan, "ok" | "warn" | "sky" | "muted"> = {
  issued: "ok",
  ready: "ok",
  partly_ready: "warn",
  awaiting: "sky",
  to_source: "muted",
};

/** Unissued goods-receipts — the non-warehouse half of what's issuable. */
function receivedUnissued(r: Row): number {
  return Math.max(0, r.receivedQtyMilli - r.issuedQtyMilli);
}

/** The one-line answer to "where does this material stand?". */
function planSentence(r: Row): { headline: string; detail: string } {
  switch (r.plan) {
    case "issued":
      return {
        headline: "Issued to cutting",
        detail: `${milliToQty(r.issuedQtyMilli, r.uom)} issued`,
      };
    case "ready": {
      const fromStore = r.onHandQtyMilli > 0;
      const fromReceipt = receivedUnissued(r) > 0;
      const where =
        fromStore && fromReceipt
          ? `${milliToQty(r.onHandQtyMilli, r.uom)} in store + ${milliToQty(receivedUnissued(r), r.uom)} received`
          : fromStore
            ? `${milliToQty(r.onHandQtyMilli, r.uom)} in store`
            : `${milliToQty(receivedUnissued(r), r.uom)} received`;
      return { headline: "Ready to issue", detail: where };
    }
    case "partly_ready":
      return {
        headline: `Short ${milliToQty(r.toOrderQtyMilli, r.uom)}`,
        detail: `${milliToQty(r.availableQtyMilli, r.uom)} available of ${milliToQty(r.stillNeededQtyMilli, r.uom)} needed`,
      };
    case "awaiting":
      return {
        headline: r.vendorName ? `Waiting on ${r.vendorName}` : "Waiting on delivery",
        detail: `${milliToQty(Math.max(0, r.orderedQtyMilli - r.receivedQtyMilli), r.uom)} on the way`,
      };
    case "to_source":
      return {
        headline: "Not sourced yet",
        detail: `Need ${milliToQty(r.stillNeededQtyMilli, r.uom)}`,
      };
  }
}

/** One sentence for the whole style — what, if anything, is blocking cutting. */
function rollUp(lines: Row[]): string {
  const n = (p: MaterialPlan) => lines.filter((l) => l.plan === p).length;
  const done = n("issued");
  const ready = n("ready");
  const waiting = n("awaiting");
  const toOrder = n("to_source") + n("partly_ready");

  if (lines.length > 0 && done === lines.length) return "Everything issued to cutting.";
  const parts: string[] = [];
  if (ready + done > 0) parts.push(`${ready + done} of ${lines.length} ready`);
  if (waiting > 0) parts.push(`${waiting} waiting on delivery`);
  if (toOrder > 0) parts.push(`${toOrder} to order`);
  return parts.length > 0 ? parts.join(" · ") : "Nothing sourced yet.";
}

export function MaterialsSection({ styleId, styleStatus }: MaterialsSectionProps) {
  const utils = trpc.useUtils();
  const query = trpc.materials.getForStyle.useQuery({ styleId });
  const vendors = trpc.vendors.list.useQuery({});
  const [poRow, setPoRow] = useState<Row | null>(null);
  const [issueRow, setIssueRow] = useState<Row | null>(null);
  const [haveRow, setHaveRow] = useState<Row | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const invalidate = async () => {
    await Promise.all([
      utils.materials.getForStyle.invalidate({ styleId }),
      utils.styles.get.invalidate({ id: styleId }),
      // Warehouse balances move on issue/declare — the issue dialog reads them.
      utils.stock.invalidate(),
      utils.auditLog.list.invalidate(),
    ]);
  };
  const onErr = (verb: string) => (e: { message: string }) =>
    toast.error(`Couldn't ${verb}`, { description: e.message });

  const convert = trpc.materials.convertToPo.useMutation({
    onSuccess: async () => {
      setPoRow(null);
      await invalidate();
      toast.success("Vendor PO raised", { description: "Sent to the vendor — receive it from Goods receipts." });
    },
    onError: onErr("raise the PO"),
  });
  const issue = trpc.materials.issueToCutting.useMutation({
    onSuccess: async () => { setIssueRow(null); await invalidate(); toast.success("Issued to cutting"); },
    onError: onErr("issue"),
  });
  const issueStock = trpc.materials.issueFromStock.useMutation({
    onSuccess: async () => { setIssueRow(null); await invalidate(); toast.success("Issued to cutting", { description: "Taken out of warehouse stock." }); },
    onError: onErr("issue"),
  });
  const declare = trpc.materials.declareInStock.useMutation({
    onSuccess: async (res: { createdItem: boolean; createdWarehouse: boolean; enabledStockTracking: boolean }) => {
      setHaveRow(null);
      await invalidate();
      const extras = [
        res.createdWarehouse ? "created a Main store" : null,
        res.createdItem ? "added it to your items" : null,
        res.enabledStockTracking ? "turned on stock tracking" : null,
      ].filter(Boolean);
      // Report the setup it had to do rather than doing it silently.
      toast.success(
        "Added to stock",
        extras.length > 0 ? { description: `Also ${extras.join(", ")}.` } : {},
      );
    },
    onError: onErr("record that stock"),
  });
  const shortage = trpc.materials.reportShortage.useMutation({
    onSuccess: async () => { await invalidate(); toast.success("Shortage flagged"); },
    onError: onErr("flag the shortage"),
  });

  // The PP gate. Ordering and declaring stock stay open — only cutting waits.
  // Without this the screen offered "Issue to cutting" on an unapproved style
  // and let the server refuse it, which teaches the user nothing until they've
  // already filled in a quantity.
  const ppApproved = PAST_PP_GATE.has(styleStatus);

  if (styleStatus === "draft" || styleStatus === "po_confirmed") {
    return (
      <div className="rounded-sm border border-dashed border-line bg-bg px-4 py-6 text-center">
        <p className="text-sm font-medium text-ink-2">Lock the costing to start sourcing</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted">
          The bill of materials is generated when costing is locked; vendor POs, warehouse stock, and
          issue-to-cutting all hang off it.
        </p>
      </div>
    );
  }

  if (query.isLoading) return <p className="text-sm text-muted">Loading sourcing…</p>;
  const data = query.data;
  if (!data || !data.hasBom || data.lines.length === 0) {
    return <p className="text-sm text-muted">No BOM lines to source.</p>;
  }

  const vendorOptions: VendorOption[] = vendors.data?.items ?? [];
  const busy = convert.isPending || issue.isPending || issueStock.isPending || declare.isPending || shortage.isPending;
  const lines = data.lines as Row[];

  const groups = [
    ...KIND_GROUPS.map((g) => ({ ...g, rows: lines.filter((r) => r.kind === g.key) })),
    { key: "other", label: "Other", rows: lines.filter((r) => !KIND_GROUPS.some((g) => g.key === r.kind)) },
  ].filter((g) => g.rows.length > 0);

  const allDone = lines.every((l) => l.plan === "issued");
  const blocked = lines.some((l) => l.plan === "to_source" || l.plan === "partly_ready");

  return (
    <div className="flex flex-col gap-4">
      {/* One sentence for the whole style, before any detail. */}
      <div
        className={`flex items-center gap-2.5 rounded-md border px-4 py-3 ${
          allDone
            ? "border-emerald-100 bg-emerald-50/60"
            : blocked
              ? "border-amber-100 bg-warn-50/60"
              : "border-sky-100 bg-sky-50/50"
        }`}
      >
        <PackageCheck size={16} className={allDone ? "text-emerald-600" : blocked ? "text-warn" : "text-sky-600"} />
        <span className="text-sm font-medium text-ink">{rollUp(lines)}</span>
      </div>

      {groups.map((g) => (
        <div key={g.key}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">{g.label}</p>
          <div className="overflow-hidden rounded-md border border-line">
            <table className="w-full border-collapse text-sm">
              <tbody>
                {g.rows.map((r) => {
                  const s = planSentence(r);
                  const isOpen = expanded === r.bomLineId;
                  return (
                    <MaterialRow
                      key={r.bomLineId}
                      row={r}
                      sentence={s}
                      open={isOpen}
                      busy={busy}
                      onToggle={() => setExpanded(isOpen ? null : r.bomLineId)}
                      onOrder={() => setPoRow(r)}
                      onIssue={() => setIssueRow(r)}
                      ppApproved={ppApproved}
                      onHave={() => setHaveRow(r)}
                      onFlag={() => shortage.mutate({ bomLineId: r.bomLineId })}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <RaisePoDialog
        row={poRow}
        vendorOptions={vendorOptions}
        vendorsLoading={vendors.isLoading}
        busy={busy}
        onOpenChange={(open) => { if (!open) setPoRow(null); }}
        onSubmit={(v) => {
          convert.mutate({
            bomLineId: v.bomLineId,
            vendorId: v.vendorId,
            ratePaisePerUnit: v.ratePaise,
            taxRatePercent: v.taxPercent,
            orderQtyMilli: v.orderQtyMilli,
            send: true,
          });
        }}
      />
      <IssueDialog
        row={issueRow}
        busy={busy}
        onOpenChange={(open) => { if (!open) setIssueRow(null); }}
        onSubmitStock={(v) => issueStock.mutate(v)}
        onSubmitReceived={(v) => issue.mutate(v)}
      />
      <HaveItDialog
        row={haveRow}
        busy={busy}
        onOpenChange={(open) => { if (!open) setHaveRow(null); }}
        onSubmit={(v) => declare.mutate(v)}
      />
    </div>
  );
}

/* ── One material ─────────────────────────────────────────────────────── */

function MaterialRow({
  row, sentence, open, busy, onToggle, onOrder, onIssue, onHave, onFlag, ppApproved,
}: {
  row: Row;
  sentence: { headline: string; detail: string };
  open: boolean;
  busy: boolean;
  onToggle: () => void;
  onOrder: () => void;
  onIssue: () => void;
  ppApproved: boolean;
  onHave: () => void;
  onFlag: () => void;
}) {
  const canIssue = row.plan !== "issued" && row.availableQtyMilli > 0;

  return (
    <>
      <tr className="border-b border-line last:border-0 align-middle">
        {/* Material — the whole cell toggles the detail. */}
        <td className="py-2.5 pl-2 pr-3">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="flex items-center gap-1.5 text-left"
          >
            {open ? (
              <ChevronDown size={14} className="shrink-0 text-muted" />
            ) : (
              <ChevronRight size={14} className="shrink-0 text-muted" />
            )}
            <span>
              <span className="block font-medium text-ink">{row.label}</span>
              <span className="block text-xs capitalize text-muted">{row.kind}</span>
            </span>
          </button>
        </td>

        <td className="whitespace-nowrap px-3 py-2.5 text-right font-mono text-sm text-ink-2">
          {milliToQty(row.requiredQtyMilli, row.uom)}
        </td>

        <td className="px-3 py-2.5">
          <StatusPill tone={PLAN_TONE[row.plan]} size="sm" dot>{sentence.headline}</StatusPill>
          <span className="mt-0.5 block text-xs text-muted">{sentence.detail}</span>
        </td>

        {/* Exactly one obvious next step. */}
        <td className="py-2.5 pl-3 pr-2 text-right">
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {row.plan === "ready" ? (
              ppApproved ? (
                <Button size="sm" disabled={busy} onClick={onIssue}>
                  <Scissors size={13} /> Issue to cutting
                </Button>
              ) : (
                <span className="flex items-center gap-1.5 text-xs text-muted">
                  <Lock size={11} /> Approve PP to issue
                </span>
              )
            ) : null}
            {row.plan === "to_source" ? (
              <>
                <Button size="sm" intent="ghost" className="border border-line" disabled={busy} onClick={onHave}>
                  <Warehouse size={13} /> I already have this
                </Button>
                <Button size="sm" disabled={busy} onClick={onOrder}>
                  <PackagePlus size={13} /> Order
                </Button>
              </>
            ) : null}
            {row.plan === "partly_ready" ? (
              <Button size="sm" disabled={busy} onClick={onOrder}>
                <PackagePlus size={13} /> Order {milliToQty(row.toOrderQtyMilli, row.uom)}
              </Button>
            ) : null}
            {row.plan === "awaiting" && row.primaryPurchaseOrderId ? (
              <Button size="sm" asChild>
                <Link to="/procurement/goods-receipts/new" search={{ po: row.primaryPurchaseOrderId }}>
                  <TruckIcon size={13} /> Record arrival
                </Link>
              </Button>
            ) : null}
            {row.plan === "awaiting" && !row.primaryPurchaseOrderId ? (
              <span className="text-xs text-muted">On order</span>
            ) : null}
            {row.plan === "issued" ? <span className="text-xs text-muted">Done</span> : null}
          </div>
        </td>
      </tr>

      {/* The numbers + every alternative — one predictable click, not a hidden menu. */}
      {open ? (
        <tr className="border-b border-line last:border-0 bg-bg/40">
          <td colSpan={4} className="px-4 py-3">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs sm:grid-cols-4">
                <Detail label="In store" value={milliToQty(row.onHandQtyMilli, row.uom)} />
                <Detail label="Ordered" value={milliToQty(row.orderedQtyMilli, row.uom)} />
                <Detail label="Received" value={milliToQty(row.receivedQtyMilli, row.uom)} />
                <Detail label="Issued" value={milliToQty(row.issuedQtyMilli, row.uom)} />
              </dl>

              <div className="flex flex-wrap items-center gap-1.5">
                {ppApproved && canIssue && row.plan !== "ready" ? (
                  <SmallBtn icon={<Scissors size={12} />} label="Issue what's here" onClick={onIssue} disabled={busy} />
                ) : null}
                {row.plan !== "issued" && row.plan !== "to_source" ? (
                  <SmallBtn icon={<Warehouse size={12} />} label="I already have more" onClick={onHave} disabled={busy} />
                ) : null}
                {row.plan !== "to_source" && row.plan !== "partly_ready" ? (
                  <SmallBtn icon={<PackagePlus size={12} />} label={row.poCount > 0 ? "Top-up PO" : "Order"} onClick={onOrder} disabled={busy} />
                ) : null}
                {row.primaryPurchaseOrderId && row.plan !== "awaiting" ? (
                  <Link
                    to="/procurement/goods-receipts/new"
                    search={{ po: row.primaryPurchaseOrderId }}
                    className="inline-flex items-center gap-1 rounded-sm border border-line bg-paper px-2 py-1 text-xs font-medium text-ink-2 transition hover:bg-bg"
                  >
                    <TruckIcon size={12} /> Record arrival
                  </Link>
                ) : null}
                {row.plan === "partly_ready" ? (
                  <SmallBtn icon={<TriangleAlert size={12} />} label="Flag shortage" onClick={onFlag} disabled={busy} tone="warn" />
                ) : null}
              </div>
            </div>

            <p className="mt-2.5 text-xs text-muted">
              {row.vendorName ? `Supplier: ${row.vendorName}. ` : ""}
              {row.itemId
                ? "Linked to an inventory item, so warehouse stock counts towards this line."
                : "Not linked to an inventory item yet — “I already have this” will add one and link it."}
            </p>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="font-mono text-ink-2">{value}</dd>
    </div>
  );
}

function SmallBtn({
  icon, label, onClick, disabled, tone,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: "warn";
}) {
  const toneCls = tone === "warn"
    ? "border-amber-200 text-warn hover:bg-warn-50"
    : "border-line text-ink-2 hover:bg-bg";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1 rounded-sm border bg-paper px-2 py-1 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${toneCls}`}
    >
      {icon} {label}
    </button>
  );
}

/* ── "I already have this" ────────────────────────────────────────────── */

function HaveItDialog({
  row, busy, onOpenChange, onSubmit,
}: {
  row: Row | null;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (v: { bomLineId: string; qtyMilli: number; warehouseId?: string | null }) => void;
}) {
  return (
    <DialogRoot open={row !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        {row ? <HaveItForm key={row.bomLineId} row={row} busy={busy} onCancel={() => onOpenChange(false)} onSubmit={onSubmit} /> : null}
      </DialogContent>
    </DialogRoot>
  );
}

function HaveItForm({
  row, busy, onCancel, onSubmit,
}: {
  row: Row;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (v: { bomLineId: string; qtyMilli: number; warehouseId?: string | null }) => void;
}) {
  // Default to what's still missing — the common case is "I have exactly what
  // this order needs", not "I have some arbitrary amount".
  const suggested = Math.max(row.toOrderQtyMilli, 0);
  const [qty, setQty] = useState(suggested > 0 ? trimUnits(suggested / 1000) : "");
  const [warehouseId, setWarehouseId] = useState<string>("");

  const warehouses = trpc.warehouses.list.useQuery({});
  const options = warehouses.data?.warehouses ?? [];
  const needsPick = options.length > 1;

  const qtyNum = Number(qty);
  const qtyValid = Number.isFinite(qtyNum) && qtyNum > 0;
  const canSubmit = !busy && qtyValid && (!needsPick || warehouseId !== "");

  return (
    <>
      <DialogHeader>
        <DialogTitle>How much do you already have?</DialogTitle>
        <DialogDescription className="mt-1.5">
          {row.label} — recorded as stock in your store, so you can issue it to cutting without
          raising a purchase order.
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-3">
        <DialogField label={`Quantity (${row.uom})`} required>
          <Input
            value={qty}
            onChange={(e) => setQty(e.currentTarget.value)}
            inputMode="decimal"
            placeholder="0"
            className="h-9 text-sm"
            disabled={busy}
          />
        </DialogField>

        {needsPick ? (
          <DialogField label="Store" required>
            <Select value={warehouseId} onValueChange={setWarehouseId} disabled={busy}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Where is it?" />
              </SelectTrigger>
              <SelectContent>
                {options.map((w: { id: string; name: string }) => (
                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DialogField>
        ) : null}

        {/* Never do setup silently — say what this will create. */}
        {!row.itemId || options.length === 0 ? (
          <p className="rounded-sm border border-sky-100 bg-sky-50/50 px-3 py-2 text-xs leading-relaxed text-ink-2">
            This will also
            {options.length === 0 ? " create a “Main store” to hold it" : ""}
            {!row.itemId && options.length === 0 ? " and" : ""}
            {!row.itemId ? ` add “${row.label}” to your items so its stock can be tracked` : ""}.
          </p>
        ) : null}
      </div>

      <DialogFooter>
        <Button type="button" intent="ghost" disabled={busy} onClick={onCancel}>Cancel</Button>
        <Button
          type="button"
          disabled={!canSubmit}
          onClick={() =>
            onSubmit({
              bomLineId: row.bomLineId,
              qtyMilli: Math.round(qtyNum * 1000),
              ...(needsPick ? { warehouseId } : {}),
            })
          }
        >
          {busy ? "Saving…" : "Add to stock"}
        </Button>
      </DialogFooter>
    </>
  );
}

/* ── Issue to cutting (one dialog; it picks the source) ───────────────── */

function IssueDialog({
  row, busy, onOpenChange, onSubmitStock, onSubmitReceived,
}: {
  row: Row | null;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmitStock: (v: { bomLineId: string; warehouseId: string; qtyMilli: number; ratePaise: string }) => void;
  onSubmitReceived: (v: { bomLineId: string; issuedQtyMilli: number; pinRef: string | null }) => void;
}) {
  return (
    <DialogRoot open={row !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        {row ? (
          <IssueForm
            key={row.bomLineId}
            row={row}
            busy={busy}
            onCancel={() => onOpenChange(false)}
            onSubmitStock={onSubmitStock}
            onSubmitReceived={onSubmitReceived}
          />
        ) : null}
      </DialogContent>
    </DialogRoot>
  );
}

function IssueForm({
  row, busy, onCancel, onSubmitStock, onSubmitReceived,
}: {
  row: Row;
  busy: boolean;
  onCancel: () => void;
  onSubmitStock: (v: { bomLineId: string; warehouseId: string; qtyMilli: number; ratePaise: string }) => void;
  onSubmitReceived: (v: { bomLineId: string; issuedQtyMilli: number; pinRef: string | null }) => void;
}) {
  // Warehouse balances for this item — only relevant when the line is linked.
  const balances = trpc.stock.summary.useQuery(
    { itemId: row.itemId ?? "", limit: 20, tab: "all" },
    { enabled: !!row.itemId && row.onHandQtyMilli > 0 },
  );
  // `stock.summary` reports whole units; the rest of Sourcing works in millis.
  const stockRows = (balances.data?.items ?? []).map((b) => ({
    warehouseId: b.warehouseId,
    warehouseName: b.warehouseName,
    onHandMilli: b.quantityOnHand * 1000,
    valuationRatePaise: b.valuationRatePaise,
  }));

  const fromReceipt = receivedUnissued(row);
  const hasStock = stockRows.length > 0;

  /**
   * Prefer warehouse stock — it's the real inventory, and depleting it first
   * keeps on-hand honest. Held as a nullable *override* rather than seeded
   * state: `balances` is async, so seeding from `hasStock` on first render
   * pinned the source to "goods received" (0 available) before the stock query
   * had resolved, and nothing moved it back.
   */
  const [sourceOverride, setSourceOverride] = useState<"stock" | "received" | null>(null);
  const [warehouseId, setWarehouseId] = useState<string>("");
  const picked = stockRows.find((w) => w.warehouseId === warehouseId) ?? stockRows[0];
  const effectiveSource = !hasStock ? "received" : (sourceOverride ?? "stock");

  const ceiling =
    effectiveSource === "stock"
      ? Math.min(picked?.onHandMilli ?? 0, row.stillNeededQtyMilli)
      : Math.min(fromReceipt, row.stillNeededQtyMilli);

  const [qty, setQty] = useState("");
  const value = qty === "" ? trimUnits(Math.max(ceiling, 0) / 1000) : qty;
  const qtyNum = Number(value);
  const qtyValid = Number.isFinite(qtyNum) && qtyNum > 0;
  const overCeiling = qtyValid && Math.round(qtyNum * 1000) > ceiling;
  const canSubmit =
    !busy && qtyValid && !overCeiling && (effectiveSource === "received" || !!(picked ?? warehouseId));

  const submit = () => {
    const milli = Math.round(qtyNum * 1000);
    if (effectiveSource === "stock" && picked) {
      onSubmitStock({
        bomLineId: row.bomLineId,
        warehouseId: picked.warehouseId,
        qtyMilli: milli,
        ratePaise: picked.valuationRatePaise,
      });
      return;
    }
    onSubmitReceived({ bomLineId: row.bomLineId, issuedQtyMilli: milli, pinRef: null });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Issue to cutting</DialogTitle>
        <DialogDescription className="mt-1.5">
          {row.label} — {milliToQty(row.stillNeededQtyMilli, row.uom)} still to reach cutting.
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-3">
        {/* Say where it's coming from rather than making the user pick between
            two identically-named buttons, as the old screen did. */}
        {hasStock && fromReceipt > 0 ? (
          <DialogField label="Take it from">
            <Select value={effectiveSource} onValueChange={(v) => { setSourceOverride(v as "stock" | "received"); setQty(""); }} disabled={busy}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="stock">Warehouse stock ({milliToQty(row.onHandQtyMilli, row.uom)})</SelectItem>
                <SelectItem value="received">Goods received ({milliToQty(fromReceipt, row.uom)})</SelectItem>
              </SelectContent>
            </Select>
          </DialogField>
        ) : (
          <p className="rounded-sm border border-line bg-bg px-3 py-2 text-xs text-ink-2">
            From {effectiveSource === "stock" ? "warehouse stock" : "goods received"} —{" "}
            <span className="font-mono">{milliToQty(ceiling, row.uom)}</span> available.
          </p>
        )}

        {effectiveSource === "stock" && stockRows.length > 1 ? (
          <DialogField label="Store" required>
            <Select value={picked?.warehouseId ?? ""} onValueChange={setWarehouseId} disabled={busy}>
              <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Pick a store" /></SelectTrigger>
              <SelectContent>
                {stockRows.map((w) => (
                  <SelectItem key={w.warehouseId} value={w.warehouseId}>
                    {w.warehouseName} — {milliToQty(w.onHandMilli, row.uom)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DialogField>
        ) : null}

        <DialogField label={`Quantity (${row.uom})`} required>
          <Input
            value={value}
            onChange={(e) => setQty(e.currentTarget.value)}
            inputMode="decimal"
            className="h-9 text-sm"
            disabled={busy}
          />
        </DialogField>

        {overCeiling ? (
          <p className="text-xs font-medium text-warn">
            Only {milliToQty(ceiling, row.uom)} can be issued from here.
          </p>
        ) : null}
      </div>

      <DialogFooter>
        <Button type="button" intent="ghost" disabled={busy} onClick={onCancel}>Cancel</Button>
        <Button type="button" disabled={!canSubmit} onClick={submit}>{busy ? "Working…" : "Issue"}</Button>
      </DialogFooter>
    </>
  );
}

/* ── Raise / Top-up PO ────────────────────────────────────────────────── */

function RaisePoDialog({
  row, vendorOptions, vendorsLoading, busy, onOpenChange, onSubmit,
}: {
  row: Row | null;
  vendorOptions: ReadonlyArray<VendorOption>;
  vendorsLoading: boolean;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (v: { bomLineId: string; vendorId: string; ratePaise: string; taxPercent: string; orderQtyMilli: number }) => void;
}) {
  return (
    <DialogRoot open={row !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        {row ? (
          <RaisePoForm
            key={row.bomLineId}
            row={row}
            vendorOptions={vendorOptions}
            vendorsLoading={vendorsLoading}
            busy={busy}
            onCancel={() => onOpenChange(false)}
            onSubmit={onSubmit}
          />
        ) : null}
      </DialogContent>
    </DialogRoot>
  );
}

function RaisePoForm({
  row, vendorOptions, vendorsLoading, busy, onCancel, onSubmit,
}: {
  row: Row;
  vendorOptions: ReadonlyArray<VendorOption>;
  vendorsLoading: boolean;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (v: { bomLineId: string; vendorId: string; ratePaise: string; taxPercent: string; orderQtyMilli: number }) => void;
}) {
  const isTopUp = row.poCount > 0;
  // What's genuinely left to buy, after stock and anything already on order —
  // so a PO never re-buys material that's already sitting in the store.
  const defaultMilli = Math.max(row.toOrderQtyMilli - Math.max(0, row.orderedQtyMilli - row.receivedQtyMilli), 0);

  const [vendorId, setVendorId] = useState("");
  const [orderQty, setOrderQty] = useState(defaultMilli > 0 ? trimUnits(defaultMilli / 1000) : "");
  const [rate, setRate] = useState("");
  const [tax, setTax] = useState("5");

  const ratePaise = rupeesToPaiseString(rate);
  const qtyNum = Number(orderQty);
  const qtyValid = Number.isFinite(qtyNum) && qtyNum > 0;
  const taxNum = Number(tax);
  const taxValid = Number.isFinite(taxNum) && taxNum >= 0;
  const ratePositive = ratePaise !== null && ratePaise !== "0";

  const subtotalPaise = ratePositive && qtyValid ? Math.round(Number(ratePaise) * qtyNum) : null;
  const totalPaise = subtotalPaise !== null && taxValid ? Math.round(subtotalPaise * (1 + taxNum / 100)) : null;

  const canSubmit = !busy && vendorId !== "" && ratePositive && qtyValid && taxValid;

  const submit = () => {
    if (!canSubmit || ratePaise === null) {
      toast.error("Pick a vendor, quantity and a valid rate");
      return;
    }
    onSubmit({ bomLineId: row.bomLineId, vendorId, ratePaise, taxPercent: tax || "5", orderQtyMilli: Math.round(qtyNum * 1000) });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{isTopUp ? "Raise a top-up PO" : "Raise vendor PO"}</DialogTitle>
        <DialogDescription className="mt-1.5">
          {row.label}
          {isTopUp ? " — an existing order is short of the requirement." : " — sent to the vendor; receive it from Goods receipts."}
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-4 gap-x-3 gap-y-1.5 rounded-sm border border-line bg-bg px-3 py-2.5 text-center">
        <Stat label="Required" value={milliToQty(row.requiredQtyMilli, row.uom)} />
        <Stat label="In store" value={milliToQty(row.onHandQtyMilli, row.uom)} />
        <Stat label="Ordered" value={milliToQty(row.orderedQtyMilli, row.uom)} />
        <Stat label="Received" value={milliToQty(row.receivedQtyMilli, row.uom)} />
      </div>
      {row.availableQtyMilli > 0 || row.orderedQtyMilli > 0 ? (
        <p className="mt-1.5 text-xs leading-relaxed text-muted">
          Suggested {milliToQty(defaultMilli, row.uom)} — what's left after everything already
          available or on order.
        </p>
      ) : null}

      <div className="mt-3 flex flex-col gap-3">
        <DialogField label="Vendor" required>
          <Select value={vendorId} onValueChange={setVendorId} disabled={vendorsLoading || busy}>
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder={vendorsLoading ? "Loading vendors…" : "Select vendor"} />
            </SelectTrigger>
            <SelectContent>
              {vendorOptions.map((v) => (
                <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DialogField>

        <div className="grid grid-cols-[1fr_1fr_auto] gap-3">
          <DialogField label={`Order qty (${row.uom})`} required>
            <Input value={orderQty} onChange={(e) => setOrderQty(e.currentTarget.value)} inputMode="decimal" placeholder="0" className="h-9 text-sm" disabled={busy} />
          </DialogField>
          <DialogField label={`Rate (₹/${row.uom})`} required>
            <Input value={rate} onChange={(e) => setRate(e.currentTarget.value)} inputMode="decimal" placeholder="240" className="h-9 text-sm" disabled={busy} />
          </DialogField>
          <DialogField label="GST %">
            <Input value={tax} onChange={(e) => setTax(e.currentTarget.value)} inputMode="decimal" className="h-9 w-16 text-sm" disabled={busy} />
          </DialogField>
        </div>

        <div className="flex items-center justify-between rounded-sm border border-sky-100 bg-sky-50/50 px-3 py-2 text-sm">
          <span className="text-muted">Order total {taxValid && taxNum > 0 ? `(incl. ${trimUnits(taxNum)}% GST)` : ""}</span>
          <span className="font-mono font-semibold text-ink">{totalPaise !== null ? paiseToInr(totalPaise.toString()) : "—"}</span>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" intent="ghost" disabled={busy} onClick={onCancel}>Cancel</Button>
        <Button type="button" disabled={!canSubmit} onClick={submit}>{busy ? "Working…" : "Raise & send"}</Button>
      </DialogFooter>
    </>
  );
}

/* ── shared bits ──────────────────────────────────────────────────────── */

function DialogField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">
        {label}
        {required ? <span className="text-bad"> *</span> : null}
      </span>
      {children}
    </label>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className="font-mono text-sm text-ink">{value}</div>
    </div>
  );
}

/** Drop a trailing `.0` from a units value used as an input default. */
function trimUnits(units: number): string {
  return Number.isInteger(units) ? String(units) : String(Number(units.toFixed(3)));
}
